import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useSyncEngine } from "./context";
import { getModel } from "./registry";
import type { ModelName, RecordOf, SyncRecord } from "./types";

const noopUnsubscribe = () => {};

/** One record; re-renders only when that record changes. */
export function useRecord<N extends ModelName>(model: N, id: string | null | undefined) {
	const { pool } = useSyncEngine();
	const subscribe = useCallback(
		(listener: () => void) => (id ? pool.subscribeRecord(model, id, listener) : noopUnsubscribe),
		[pool, model, id],
	);
	return useSyncExternalStore(subscribe, () => (id ? pool.get(model, id) : undefined)) as
		| RecordOf<N>
		| undefined;
}

type ListOptions<N extends ModelName> = {
	/** Equality filter. Uses the model's index when the first key is declared in `indexes`. */
	where?: Partial<RecordOf<N>>;
	/** Extra predicate. Keep it stable (`useCallback`) to avoid recomputing every render. */
	filter?: (record: RecordOf<N>) => boolean;
	sort?: (a: RecordOf<N>, b: RecordOf<N>) => number;
};

/** All records of a model (optionally filtered). Re-renders when any record of the model changes. */
export function useRecords<N extends ModelName>(
	model: N,
	options: ListOptions<N> = {},
): RecordOf<N>[] {
	const { pool } = useSyncEngine();
	const subscribe = useCallback(
		(listener: () => void) => pool.subscribeModel(model, listener),
		[pool, model],
	);
	const all = useSyncExternalStore(subscribe, () => pool.list(model));
	const whereKey = JSON.stringify(options.where ?? null);
	const { filter, sort } = options;

	return useMemo(() => {
		const where = Object.entries((JSON.parse(whereKey) as Record<string, unknown> | null) ?? {});
		const matches = (r: SyncRecord) => where.every(([key, value]) => r[key] === value);
		let records: SyncRecord[] = all;
		if (where.length) {
			const ids = pool.idsWhere(model, where[0][0], where[0][1]);
			if (ids) {
				records = ids
					.map((id) => pool.get(model, id))
					.filter((r): r is SyncRecord => !!r && matches(r));
				const modelSort = getModel(model)?.sort as
					| ((a: SyncRecord, b: SyncRecord) => number)
					| undefined;
				if (modelSort) records.sort(modelSort);
			} else {
				records = all.filter(matches);
			}
		}
		let typed = records as unknown as RecordOf<N>[];
		if (filter) typed = typed.filter(filter);
		if (sort) typed = [...typed].sort(sort);
		return typed;
	}, [all, whereKey, filter, sort, pool, model]);
}

/**
 * Ids only, with a stable array identity while membership/order is unchanged.
 * Pair with row components calling `useRecord` so an edit re-renders one row, not the list.
 */
export function useRecordIds<N extends ModelName>(
	model: N,
	options: ListOptions<N> = {},
): string[] {
	const records = useRecords(model, options);
	const previous = useRef<string[]>([]);
	const ids = records.map((r) => (r as { id: string }).id);
	const same =
		ids.length === previous.current.length && ids.every((id, i) => id === previous.current[i]);
	if (!same) previous.current = ids;
	return previous.current;
}

export function useSyncStatus() {
	const engine = useSyncEngine();
	return useSyncExternalStore(engine.subscribeStatus, engine.getSnapshot);
}

/** Typed optimistic mutations for one model. Returns synchronously; the engine handles the network. */
export function useModelActions<N extends ModelName>(model: N) {
	const engine = useSyncEngine();
	return useMemo(
		() => ({
			create: (input: Partial<RecordOf<N>>) => engine.create(model, input),
			update: (id: string, patch: Partial<RecordOf<N>>) => engine.update(model, id, patch),
			remove: (id: string) => engine.delete(model, id),
		}),
		[engine, model],
	);
}

/** Ensures a lazy model's rows for a project are loaded. `ready` is true immediately when cached. */
export function usePartial(model: ModelName, projectId: string | undefined) {
	const engine = useSyncEngine();
	const [ready, setReady] = useState(() => !projectId || engine.isPartialLoaded(model, projectId));
	useEffect(() => {
		if (!projectId) return;
		if (engine.isPartialLoaded(model, projectId)) return setReady(true);
		let active = true;
		setReady(false);
		engine.ensurePartial(model, projectId).finally(() => active && setReady(true));
		return () => {
			active = false;
		};
	}, [engine, model, projectId]);
	return { ready };
}
